import create from 'zustand';
import { Line, LineCreate, LineUpdate, LineWithCount } from '../types/line';
import { lineApi } from '../api/lineApi';

interface LineState {
  lines: Line[];
  currentLine: LineWithCount | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchLines: (params?: { 
    skip?: number; 
    limit?: number; 
    project_id?: number;
    status?: boolean;
  }) => Promise<void>;
  fetchLine: (id: number) => Promise<void>;
  createLine: (data: LineCreate) => Promise<void>;
  updateLine: (id: number, data: LineUpdate) => Promise<void>;
  deleteLine: (id: number) => Promise<void>;
  clearCurrentLine: () => void;
  clearLines: () => void;
}

export const useLineStore = create<LineState>((set, get) => ({
  lines: [],
  currentLine: null,
  isLoading: false,
  error: null,

  fetchLines: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const lines = await lineApi.getLines(params);
      set({ lines });
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to fetch lines' });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchLine: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const line = await lineApi.getLine(id);
      set({ currentLine: line });
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to fetch line' });
    } finally {
      set({ isLoading: false });
    }
  },

  createLine: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const newLine = await lineApi.createLine(data);
      set(state => ({ lines: [...state.lines, newLine] }));
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to create line' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  updateLine: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const updatedLine = await lineApi.updateLine(id, data);
      set(state => ({
        lines: state.lines.map(l => l.id === id ? updatedLine : l),
        currentLine: state.currentLine?.id === id ? { ...state.currentLine, ...updatedLine } : state.currentLine
      }));
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to update line' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteLine: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await lineApi.deleteLine(id);
      set(state => ({
        lines: state.lines.filter(l => l.id !== id),
        currentLine: state.currentLine?.id === id ? null : state.currentLine
      }));
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to delete line' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  clearCurrentLine: () => {
    set({ currentLine: null });
  },

  clearLines: () => {
    set({ lines: [], currentLine: null });
  },
}));
